// From https://github.com/svecosystem/runed/blob/main/LICENSE
// MIT License
// Copyright (c) 2024 Hunter Johnston <https://github.com/huntabyte>
// Copyright (c) 2024 Thomas G. Lopes <https://github.com/tglide>
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.
import { untrack } from 'svelte';
function runEffect(flush, effect) {
    switch (flush) {
        case 'post':
            $effect(effect);
            break;
        case 'pre':
            $effect.pre(effect);
            break;
    }
}
function runWatcher(sources, flush, effect, options = {}) {
    const { lazy = false } = options;
    // Run the effect immediately if `lazy` is `false`.
    let active = !lazy;
    // On the first run, if the dependencies are an array, pass an empty array
    // to the previous value instead of `undefined` to allow destructuring.
    //
    // watch(() => [a, b], ([a, b], [prevA, prevB]) => { ... });
    let previousValues = Array.isArray(sources)
        ? []
        : undefined;
    runEffect(flush, () => {
        const values = Array.isArray(sources) ? sources.map((source) => source()) : sources();
        if (!active) {
            active = true;
            previousValues = values;
            return;
        }
        const cleanup = untrack(() => effect(values, previousValues));
        previousValues = values;
        return cleanup;
    });
}
function runWatcherOnce(sources, flush, effect) {
    const cleanupRoot = $effect.root(() => {
        let stop = false;
        runWatcher(sources, flush, (values, previousValues) => {
            if (stop) {
                cleanupRoot();
                return;
            }
            // Since `lazy` is `true`, `previousValues` is always defined.
            const cleanup = effect(values, previousValues);
            stop = true;
            return cleanup;
        }, 
        // Running the effect immediately just once makes no sense at all.
        // That's just `onMount` with extra steps.
        { lazy: true });
    });
    $effect(() => {
        return cleanupRoot;
    });
}
export function watch(sources, effect, options) {
    runWatcher(sources, 'post', effect, options);
}
function watchPre(sources, effect, options) {
    runWatcher(sources, 'pre', effect, options);
}
watch.pre = watchPre;
export function watchOnce(source, effect) {
    runWatcherOnce(source, 'post', effect);
}
function watchOncePre(source, effect) {
    runWatcherOnce(source, 'pre', effect);
}
watchOnce.pre = watchOncePre;
